// src/pages/lead-automation/LeadFormDetailPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader, AlertCircle } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { getLeadFormById, getUploadSessions } from '../../services/api/leadAutomation';
import SessionHistoryTable from './components/SessionHistoryTable';
import styles from './LeadFormDetailPage.module.css';

const PAGE_SIZE = 20;

const LeadFormDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const la = (key) => t(`leadAutomation.${key}`);

  const [leadForm, setLeadForm] = useState(null);
  const [formLoading, setFormLoading] = useState(true);
  const [formError, setFormError] = useState('');

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadForm = useCallback(async () => {
    setFormLoading(true);
    setFormError('');
    try {
      const data = await getLeadFormById(id);
      setLeadForm(data.leadForm || data);
    } catch (err) {
      setFormError(err?.response?.data?.message || la('failedToLoadLeadForms'));
    } finally {
      setFormLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessions = useCallback(async (currentPage) => {
    setSessionsLoading(true);
    setSessionsError('');
    try {
      const data = await getUploadSessions(id, { page: currentPage, limit: PAGE_SIZE });
      setSessions(data.sessions || data || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      setSessionsError(err?.response?.data?.message || la('failedToLoadSessions'));
    } finally {
      setSessionsLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadForm();
    loadSessions(1);
  }, [loadForm, loadSessions]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadSessions(newPage);
  };

  return (
    <div className={styles.page}>
      <div className={styles.breadcrumb}>
        <button className={styles.backBtn} onClick={() => navigate('/lead-automation')}>
          <ArrowLeft size={16} />
          {la('backToLeadForms')}
        </button>
      </div>

      {formLoading && (
        <div className={styles.loadingState}>
          <Loader size={20} className={styles.spin} />
        </div>
      )}

      {!formLoading && formError && (
        <div className={styles.errorState}>
          <AlertCircle size={16} />
          {formError}
        </div>
      )}

      {!formLoading && leadForm && (
        <>
          <div className={styles.pageHeader}>
            <div>
              <h1 className={styles.pageTitle}>{leadForm.name}</h1>
              <p className={styles.pageDesc}>{la('sessionHistory')}</p>
            </div>
          </div>

          <div className={styles.formMeta}>
            <span><strong>{la('sheetTab')}:</strong> {leadForm.sheetTabName}</span>
            <span><strong>{la('defaultShop')}:</strong> {leadForm.shop?.name || '—'}</span>
            <span><strong>{la('defaultProduct')}:</strong> {leadForm.defaultProduct?.name || '—'}</span>
            <span><strong>{la('lastRow')}:</strong> {leadForm.lastRowIndex}</span>
          </div>

          <div className={styles.sessionsSection}>
            {sessionsLoading && (
              <div className={styles.loadingState}>
                <Loader size={18} className={styles.spin} />
              </div>
            )}
            {!sessionsLoading && sessionsError && (
              <div className={styles.errorState}>
                <AlertCircle size={16} />
                {sessionsError}
              </div>
            )}
            {!sessionsLoading && !sessionsError && (
              <SessionHistoryTable sessions={sessions} />
            )}
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
              >
                Previous
              </button>
              <span className={styles.pageInfo}>
                {page} / {totalPages}
              </span>
              <button
                className={styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeadFormDetailPage;
